import React from 'react';
import {
  Box,
  Container,
  Stack,
  Typography,
  Button,
  Paper,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GavelIcon from '@mui/icons-material/Gavel';
import RuleIcon from '@mui/icons-material/Rule';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { LandingNavbar } from '../components/LandingNavbar';
import Footer from '../components/Footer';

const brand = {
  primary: '#800b33',
  primaryDark: '#5c0620',
  darkText: '#1e293b',
  muted: '#64748b',
  surface: '#ffffff',
  bg: '#f8fafc',
  accent: '#fdf2f8',
};

// --- Terms Content ---

// --- Terms Content ---

const PolicySection = ({ title, children, icon }) => (
  <Box sx={{ mb: 6 }}>
    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
      <Box sx={{ p: 1, borderRadius: 2, bgcolor: brand.accent, color: brand.primary }}>
        {icon || <RuleIcon />}
      </Box>
      <Typography variant="h5" sx={{ fontWeight: 700, color: brand.darkText }}>
        {title}
      </Typography>
    </Stack>
    <Box sx={{ pl: { xs: 0, md: 7 } }}>
      {children}
    </Box>
  </Box>
);

const Terms = () => {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: brand.bg }}>
      <LandingNavbar />

      {/* Header Region */}
      <Box sx={{
        py: 10,
        background: 'radial-gradient(circle at 50% -20%, rgba(128,11,51,0.08), transparent 70%)',
        textAlign: 'center'
      }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ color: brand.primary, fontWeight: 700, letterSpacing: 2 }}>
            LEGAL AGREEMENTS
          </Typography>
          <Typography variant="h2" sx={{ fontWeight: 800, color: brand.darkText, mt: 2, mb: 3, letterSpacing: '-0.03em' }}>
            Terms & Conditions
          </Typography>
          <Typography variant="body1" sx={{ color: brand.muted, fontSize: '1.2rem', maxWidth: 600, mx: 'auto' }}>
            The rules of the road. By using Savvy, you agree to build a transparent and compliant community.
          </Typography>
        </Container>
      </Box>

      {/* Content Card */}
      <Container maxWidth="md" sx={{ mb: 12, position: 'relative', top: -40 }}>
        <Paper elevation={0} sx={{
          p: { xs: 4, md: 8 },
          borderRadius: 4,
          boxShadow: '0 20px 40px -10px rgba(0,0,0,0.05)',
          border: '1px solid rgba(0,0,0,0.04)'
        }}>
          <Typography variant="body1" paragraph sx={{ color: brand.darkText, mb: 4, fontSize: '1.1rem', lineHeight: 1.8 }}>
            Last Updated: January 2025
          </Typography>

          <Divider sx={{ mb: 6 }} />

          <PolicySection title="Acceptance of Terms" icon={<HandshakeIcon />}>
            <Typography variant="body1" sx={{ color: brand.muted, lineHeight: 1.8, mb: 2 }}>
              By registering for or using the Savvy Community platform ("Service"), you agree to be bound by these Terms. If you are using the Service on behalf of a group or organization, you are agreeing to these Terms for that group and representing that you have the authority to do so.
            </Typography>
          </PolicySection>

          <PolicySection title="Group Governance" icon={<GavelIcon />}>
            <Typography variant="body1" sx={{ color: brand.muted, lineHeight: 1.8 }}>
              Savvy provides the tools, but your group provides the rules. You agree to:
            </Typography>
            <List dense>
              {[
                'Obtain necessary authorization from members before initiating transactions.',
                'Adhere to local financial regulations regarding group savings and loans.',
                'Maintain transparency in all financial records generated by the platform.'
              ].map(item => (
                <ListItem key={item}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckCircleIcon sx={{ fontSize: 18, color: brand.primary }} /></ListItemIcon>
                  <ListItemText primary={item} primaryTypographyProps={{ color: brand.muted }} />
                </ListItem>
              ))}
            </List>
          </PolicySection>

          <PolicySection title="Usage Restrictions" icon={<RuleIcon />}>
            <Typography variant="body1" sx={{ color: brand.muted, lineHeight: 1.8 }}>
              You may not use the platform for any illegal purpose, including but not limited to money laundering, fraud, or financing illicit activities. We reserve the right to suspend any account found in violation of these safety standards immediately.
            </Typography>
          </PolicySection>

          <Typography variant="body2" sx={{ color: brand.muted, mt: 4, fontStyle: 'italic' }}>
            Savvy Community reserves the right to modify these terms at any time. Continued use of the service constitutes acceptance of the new terms.
          </Typography>

        </Paper>
      </Container>

      <Footer />
    </Box>
  );
};

export default Terms;
